import React from 'react';
import {
  Box,
  type GDSHeadingLevel,
  type GDSHeadingSize,
  HeadingText,
  ParagraphText,
  type GDSTypographyIntrinsicEmphasis,
  type GDSParagraphTextSize,
  type GDSAlign,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeRendererProps,
  type MasonryEngineNode,
} from '@leagueplatform/masonry-engine';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

type MasonryContentBlockProperties = {
  body: string;
  bodyEmphasis?: GDSTypographyIntrinsicEmphasis;
  bodySize?: GDSParagraphTextSize;
  bodyTextAlignment?: GDSAlign;
  heading?: string;
  headingEmphasis?: GDSTypographyIntrinsicEmphasis;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  headingTextAlignment?: GDSAlign;
};

export type MasonryContentBlockNode = MasonryEngineNode<
  'contentBlock',
  MasonryContentBlockProperties
>;

export type MasonryContentBlockRendererProps =
  MasonryEngineNodeRendererProps<MasonryContentBlockNode>;

export const MasonryContentBlockRenderer = ({
  body,
  bodyEmphasis = 'base',
  bodySize = 'base',
  bodyTextAlignment = 'start',
  className,
  css,
  engineClassName,
  heading,
  headingEmphasis = 'base',
  headingLevel = '3',
  headingSize = 'md',
  headingTextAlignment = 'start',
  locatorId,
  rootElementRef,
}: MasonryContentBlockRendererProps) => (
  <Box
    className={formatClassNames(engineClassName, className)}
    css={css}
    data-testid={locatorId}
    ref={rootElementRef}
  >
    {heading && (
      <HeadingText
        emphasis={headingEmphasis}
        level={headingLevel}
        size={headingSize}
        css={{ textAlign: headingTextAlignment }}
      >
        {heading}
      </HeadingText>
    )}
    <ParagraphText
      css={{ textAlign: bodyTextAlignment }}
      emphasis={bodyEmphasis}
      htmlStringOptions={BodyTextHtmlTagOptions}
      size={bodySize}
    >
      {body}
    </ParagraphText>
  </Box>
);
